import TeacherPageHeader from '../../components/TeacherPageHeader';
import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../services';
import Modal from '../../components/Modal';
import Tabs from '../../components/Tabs';
import { formatDateShort } from '../../utils/formatters';
import { STATUS_LABELS } from '../../config/constants';
import { useModal } from '../../hooks/common';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { PageLoading, EmptyState } from '../../components';

interface Contact {
  contactId: string;
  nickname: string;
  subject: string;
  message: string;
  category: string;
  status: 'pending' | 'replied' | 'closed';
  createdAt: string;
  reply?: string;
  repliedAt?: string;
  priority?: 'high' | 'normal' | 'low';
  isPublic?: boolean;
  updatedAt?: string;
}

const Contacts: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [replyText, setReplyText] = useState('');
  const [isReplying, setIsReplying] = useState(false);

  // Infinite scroll hook
  const {
    items: contacts,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    handleScroll
  } = useInfiniteScroll<Contact>({
    fetchFunction: contactAPI.getContacts,
    limit: 200,
    searchTerm: '',
    statusFilter
  });

  const replyModal = useModal<Contact>();
  const isMobile = useIsMobile();

  // 데스크탑: 하단 고정 상세 패널
  const [selectedDetail, setSelectedDetail] = useState<Contact | null>(null);

  // 기본으로 가장 최신 문의의 상세 표시
  useEffect(() => {
    if (isMobile || contacts.length === 0) return;
    setSelectedDetail(prev =>
      prev && contacts.some(c => c.contactId === prev.contactId)
        ? contacts.find(c => c.contactId === prev.contactId) || contacts[0]
        : contacts[0]
    );
  }, [contacts, isMobile]);

  // 하단 패널에서 답변 등록
  const submitInlineReply = async () => {
    if (!selectedDetail || !replyText.trim()) return;
    try {
      setIsReplying(true);
      const response = await contactAPI.replyContact(selectedDetail.contactId, replyText);
      if (response.success) {
        setReplyText('');
        refresh();
        alert('답변이 성공적으로 등록되었습니다.');
      }
    } catch {
      alert('답변 전송 중 오류가 발생했습니다.');
    } finally {
      setIsReplying(false);
    }
  };

  const handleContactClick = async (contact: Contact) => {
    if (!isMobile) {
      setSelectedDetail(prev => {
        if (prev?.contactId !== contact.contactId) setReplyText('');
        return contact;
      });
      return;
    }
    // 목록의 데이터를 직접 사용 (API 호출 대신)
    replyModal.open(contact);
  };

  const handleReply = async () => {
    if (!replyModal.data || !replyText.trim()) return;

    try {
      setIsReplying(true);
      const response = await contactAPI.replyContact(replyModal.data.contactId, replyText);

      if (response.success) {
        setReplyText('');
        replyModal.close();
        refresh();
        alert('답변이 성공적으로 등록되었습니다.');
      }
    } catch {
      alert('답변 전송 중 오류가 발생했습니다.');
    } finally {
      setIsReplying(false);
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <span className={`status-badge ${status}`}>
        {STATUS_LABELS.CONTACT[status as keyof typeof STATUS_LABELS.CONTACT] || status}
      </span>
    );
  };

  const getCategoryLabel = (category: string) => {
    // API에서 오는 실제 카테고리 값들을 그대로 반환
    // 이미 한글로 되어있어서 별도 매핑이 필요없음
    return category;
  };

  if (isLoading && contacts.length === 0) {
    return <PageLoading message="문의 목록을 불러오는 중..." fullScreen />;
  }

  return (
    <div className="teacher-page list-detail-page">
      <TeacherPageHeader title="문의관리">
        <Tabs
          options={[
            { value: 'all', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'replied', label: '답변완료' }
          ]}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        />
      </TeacherPageHeader>
      {/* 필터 (모바일 전용) */}
      <div className="content-header desktop-hidden">
        <Tabs
          options={[
            { value: 'all', label: '전체' },
            { value: 'pending', label: '대기중' },
            { value: 'replied', label: '답변완료' }
          ]}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        />
      </div>

      {/* 문의 목록 */}
      <div className="requests-container" onScroll={handleScroll}>
        {error && (
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
            {error}
          </div>
        )}
        {contacts.length === 0 && !isLoading ? (
          <EmptyState
            title="접수된 문의가 없습니다"
            description="새로운 문의가 들어오면 여기에 표시됩니다."
          />
        ) : (
          <>
            {/* 데스크탑 테이블 뷰 */}
            <div className="table-view">
              <div className="table-wrapper">
                <table className="lesson-requests-table">
                  <thead>
                    <tr>
                      <th>닉네임</th>
                      <th>제목</th>
                      <th>카테고리</th>
                      <th>접수일</th>
                      <th>액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((contact) => (
                      <tr
                        key={contact.contactId}
                        className={`status-row status-${contact.status}`}
                        onClick={() => handleContactClick(contact)}
                      >
                        <td>
                          <div className="name-cell">
                            <span className="name">{contact.nickname}</span>
                          </div>
                        </td>
                        <td className="time-cell">
                          {contact.subject.length > 30 ? `${contact.subject.substring(0, 30)}...` : contact.subject}
                        </td>
                        <td>{getCategoryLabel(contact.category)}</td>
                        <td>{formatDateShort(contact.createdAt)}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {contact.status === 'pending' && (
                            <div className="action-buttons">
                              <button
                                className="btn-action approve"
                                onClick={() => handleContactClick(contact)}
                              >
                                답변
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 모바일 리스트 뷰 */}
            <div className="card-view">
              <div className="requests-list">
                {contacts.map((contact) => (
                  <div
                    key={contact.contactId}
                    className={`request-item status-${contact.status}`}
                    onClick={() => handleContactClick(contact)}
                  >
                    <div className="item-row">
                      <span className="item-name">{contact.nickname}</span>
                      <span className="item-date">{formatDateShort(contact.createdAt)}</span>
                    </div>
                    <div className="item-row">
                      <span className="item-time">
                        {contact.subject.length > 25 ? `${contact.subject.substring(0, 25)}...` : contact.subject}
                      </span>
                    </div>
                    {contact.status === 'pending' && (
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn-approve"
                          onClick={() => handleContactClick(contact)}
                          disabled={isReplying}
                        >
                          답변
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Loading more indicator */}
            {isLoadingMore && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div className="spinner">추가 데이터 로딩 중...</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 데스크탑 하단 고정 상세 */}
      {selectedDetail && (
        <div className="request-detail-inline table-view">
          <div className="detail-main">
            <div className="detail-title-row">
              <div className="detail-title">문의 상세 — {selectedDetail.nickname}</div>
              {getStatusBadge(selectedDetail.status)}
              <button className="detail-close" onClick={() => setSelectedDetail(null)} aria-label="닫기">✕</button>
            </div>
            <div className="detail-grid">
              <div>
                <div className="dt">제목</div>
                <div className="dd">{selectedDetail.subject}</div>
              </div>
              <div>
                <div className="dt">카테고리</div>
                <div className="dd">{getCategoryLabel(selectedDetail.category)}</div>
              </div>
              <div>
                <div className="dt">접수일</div>
                <div className="dd">{formatDateShort(selectedDetail.createdAt)}</div>
              </div>
              <div>
                <div className="dt">답변일</div>
                <div className="dd">{selectedDetail.repliedAt ? formatDateShort(selectedDetail.repliedAt) : '—'}</div>
              </div>
            </div>
            <div className="detail-message">{selectedDetail.message}</div>
            {selectedDetail.reply && (
              <div className="detail-reply">
                <div className="reply-label">답변 {selectedDetail.repliedAt && `(${formatDateShort(selectedDetail.repliedAt)})`}</div>
                <div className="reply-body">{selectedDetail.reply}</div>
              </div>
            )}
            {selectedDetail.status === 'pending' && (
              <div className="detail-reply-form">
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="답변 내용을 입력하세요..."
                  rows={3}
                />
              </div>
            )}
          </div>
          {selectedDetail.status === 'pending' && (
            <div className="detail-actions">
              <button
                className="approve-btn"
                onClick={submitInlineReply}
                disabled={isReplying || !replyText.trim()}
              >
                {isReplying ? '등록 중...' : '답변 등록'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* 답변 모달 */}
      <Modal
        isOpen={replyModal.isOpen}
        onClose={replyModal.close}
        title="문의 상세"
        size="sm"
      >
        {replyModal.data && (
          <div className="detail-content compact">
            <div className="detail-header">
              <div className="name-row">
                <span className="student-name">{replyModal.data.nickname}</span>
                {getStatusBadge(replyModal.data.status)}
              </div>
              <div className="meta-info">
                {getCategoryLabel(replyModal.data.category)} · {formatDateShort(replyModal.data.createdAt)}
              </div>
            </div>

            <div className="detail-info-list">
              <div className="info-row">
                <span className="label">제목</span>
                <span className="value">{replyModal.data.subject}</span>
              </div>
              <div className="info-row">
                <span className="label">내용</span>
                <span className="value">{replyModal.data.message}</span>
              </div>
            </div>

            {replyModal.data.reply && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '8px' }}>
                <div style={{ fontSize: '12px', color: '#15803d', marginBottom: '4px' }}>답변 ({formatDateShort(replyModal.data.repliedAt || '')})</div>
                <div style={{ fontSize: '14px', color: '#166534', whiteSpace: 'pre-wrap' }}>{replyModal.data.reply}</div>
              </div>
            )}

            {replyModal.data.status === 'pending' && (
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', display: 'block' }}>답변 내용</label>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="form-textarea"
                  rows={4}
                  placeholder="답변 내용을 입력하세요..."
                  style={{ minHeight: '100px' }}
                />
              </div>
            )}

            {replyModal.data.status === 'pending' && (
              <div className="modal-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleReply}
                  disabled={isReplying || !replyText.trim()}
                >
                  {isReplying ? '답변 중...' : '답변 전송'}
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Contacts; 